import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Mail, Pencil, Phone, Plus, Trash2, UserRound } from "lucide-react";
import { toast } from "sonner";

const emptyContact = () => ({
  id: "",
  name: "",
  role: "",
  phone: "",
  email: "",
  notes: "",
});

export function normalizeClientContacts(contacts) {
  if (Array.isArray(contacts)) return contacts;
  if (typeof contacts === "string" && contacts.trim()) {
    return [{ ...emptyContact(), id: `legacy-${Date.now()}`, name: contacts.trim() }];
  }
  return [];
}

export function ClientContacts({ contacts = [], onChange }) {
  const [open, setOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [form, setForm] = useState(emptyContact);

  const openCreate = () => {
    setEditingIndex(null);
    setForm(emptyContact());
    setOpen(true);
  };

  const openEdit = (contact, index) => {
    setEditingIndex(index);
    setForm({ ...emptyContact(), ...contact });
    setOpen(true);
  };

  const save = () => {
    if (!form.name.trim()) return toast.error("Indique o nome do contacto");
    const contact = {
      ...form,
      id: form.id || globalThis.crypto?.randomUUID?.() || `contact-${Date.now()}`,
      name: form.name.trim(),
    };
    if (editingIndex === null) onChange([...contacts, contact]);
    else onChange(contacts.map((item, index) => (index === editingIndex ? contact : item)));
    setOpen(false);
  };

  const remove = (index) => onChange(contacts.filter((_, itemIndex) => itemIndex !== index));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <Label>Contactos</Label>
          <p className="text-xs text-muted-foreground mt-0.5">Pessoas importantes associadas ao cliente.</p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={openCreate}>
          <Plus className="h-4 w-4 mr-1.5" /> Criar contacto
        </Button>
      </div>

      {contacts.length === 0 ? (
        <div className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
          Ainda não existem contactos.
        </div>
      ) : (
        <div className="space-y-2">
          {contacts.map((contact, index) => (
            <div key={contact.id || index} className="rounded-lg border border-border p-3 flex gap-3">
              <div className="h-9 w-9 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0">
                <UserRound className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-poppins font-medium text-sm">{contact.name}</div>
                {contact.role && <div className="text-xs text-muted-foreground">{contact.role}</div>}
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-muted-foreground">
                  {contact.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{contact.phone}</span>}
                  {contact.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{contact.email}</span>}
                </div>
                {contact.notes && <p className="text-xs text-muted-foreground mt-1.5 whitespace-pre-wrap">{contact.notes}</p>}
              </div>
              <div className="flex gap-1 shrink-0">
                <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(contact, index)} aria-label="Editar contacto">
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => remove(index)} aria-label="Eliminar contacto">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingIndex === null ? "Criar contacto" : "Editar contacto"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome</Label><Input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} className="mt-1" /></div>
            <div><Label>Cargo / relação</Label><Input value={form.role} onChange={(event) => setForm({ ...form, role: event.target.value })} className="mt-1" placeholder="Ex.: Diretora de Marketing" /></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Telemóvel</Label><Input type="tel" value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} className="mt-1" /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} className="mt-1" /></div>
            </div>
            <div><Label>Notas</Label><Textarea value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} className="mt-1" rows={3} /></div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button type="button" onClick={save} className="bg-primary text-black hover:bg-primary/90">Guardar contacto</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
